import { useCallback, useEffect, useRef, useState } from "react";
import { KeyRound, MailPlus, MoreHorizontal, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ConfirmDialog from "@/components/ConfirmDialog";
import useOrganization from "../../hooks/useOrganization";
import { createPasswordResetLink, getMembers, removeMember, revokeInvitation } from "../../utils";
import { formatDate, formatRelativeDays } from "../../utils/dates";
import { invitationLink, passwordResetLink } from "../../utils/links";
import LinkModal, { type SharedLink } from "../../fragments/LinkModal";
import InviteMemberModal from "./InviteMemberModal";

interface Member {
  id: string;
  email: string;
  createdAt: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  expiresAt: string;
}

type Confirming =
  { kind: "remove"; member: Member } | { kind: "revoke"; invitation: PendingInvitation } | null;

const Person: React.FC<{ email: string; muted?: boolean }> = ({ email, muted }) => (
  <div className="flex min-w-0 items-center gap-3">
    <span
      className={
        muted
          ? "flex size-8 shrink-0 items-center justify-center rounded-full border border-dashed text-muted-foreground"
          : "flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold uppercase"
      }
      aria-hidden="true"
    >
      {muted ? <MailPlus className="size-3.5" /> : email.slice(0, 2)}
    </span>
    <span className="truncate font-medium">{email}</span>
  </div>
);

const LoadingRows: React.FC<{ columns: number }> = ({ columns }) => (
  <>
    {Array.from({ length: 3 }, (_, i) => (
      <TableRow key={i} className="hover:bg-transparent">
        {Array.from({ length: columns }, (_, j) => (
          <TableCell key={j}>
            <Skeleton className={j === 0 ? "h-8 w-56" : "h-4 w-24"} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

const EmptyRow: React.FC<{ columns: number; children: React.ReactNode }> = ({
  columns,
  children,
}) => (
  <TableRow className="hover:bg-transparent">
    <TableCell colSpan={columns} className="py-10 text-center text-muted-foreground">
      {children}
    </TableCell>
  </TableRow>
);

const Members: React.FC = () => {
  const organization = useOrganization();
  const [data, setData] = useState<{ members: Member[]; invitations: PendingInvitation[] } | null>(
    null,
  );
  const [inviting, setInviting] = useState(false);
  const [link, setLink] = useState<SharedLink | null>(null);
  const [confirming, setConfirming] = useState<Confirming>(null);
  // Keeps the dialog's text while it animates closed.
  const lastConfirming = useRef<Confirming>(null);
  if (confirming) lastConfirming.current = confirming;
  const shown = confirming ?? lastConfirming.current;

  const load = useCallback(async () => {
    try {
      setData(await getMembers(organization));
    } catch (e) {
      toast.error((e as Error).message);
      setData({ members: [], invitations: [] });
    }
  }, [organization]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action: () => Promise<unknown>, done: string) => {
    try {
      await action();
      toast.success(done);
    } catch (e) {
      toast.error((e as Error).message);
    }
    await load();
  };

  const showResetLink = async (member: Member) => {
    try {
      const token = await createPasswordResetLink(organization, member.id);
      setLink({
        title: `Resetlink voor ${member.email}`,
        url: passwordResetLink(token),
        note: "Stuur deze link zelf naar de beheerder. Hij werkt één keer en verloopt na 24 uur. Een eerdere resetlink voor dit lid werkt dan niet meer.",
      });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Actieve leden</CardTitle>
            <CardDescription>
              {data
                ? `${data.members.length} ${data.members.length === 1 ? "lid" : "leden"} met toegang tot deze organisatie`
                : "Leden laden…"}
            </CardDescription>
          </div>
          <Button onClick={() => setInviting(true)}>
            <UserPlus />
            Lid uitnodigen
          </Button>
        </CardHeader>
        <CardContent className="px-2 pb-2">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>E-mailadres</TableHead>
                <TableHead>Lid sinds</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">Acties</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data == null ? (
                <LoadingRows columns={3} />
              ) : data.members.length === 0 ? (
                <EmptyRow columns={3}>Nog geen leden. Nodig de eerste beheerder uit.</EmptyRow>
              ) : (
                data.members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <Person email={member.email} />
                    </TableCell>
                    <TableCell className="text-muted-foreground tabular-nums">
                      {formatDate(member.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Acties voor ${member.email}`}
                          >
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onSelect={() => showResetLink(member)}>
                            <KeyRound />
                            Resetlink maken
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            variant="destructive"
                            onSelect={() => setConfirming({ kind: "remove", member })}
                          >
                            <Trash2 className="text-destructive" />
                            Verwijderen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div>
            <CardTitle>Openstaande uitnodigingen</CardTitle>
            <CardDescription>Uitnodigingen die nog niet zijn geaccepteerd.</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="px-2 pb-2">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>E-mailadres</TableHead>
                <TableHead>Verloopt</TableHead>
                <TableHead className="w-28">
                  <span className="sr-only">Acties</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data == null ? (
                <LoadingRows columns={3} />
              ) : data.invitations.length === 0 ? (
                <EmptyRow columns={3}>Geen openstaande uitnodigingen</EmptyRow>
              ) : (
                data.invitations.map((invitation) => (
                  <TableRow key={invitation.id}>
                    <TableCell>
                      <Person email={invitation.email} muted />
                    </TableCell>
                    <TableCell
                      className="text-muted-foreground"
                      title={formatDate(invitation.expiresAt)}
                    >
                      {formatRelativeDays(invitation.expiresAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setConfirming({ kind: "revoke", invitation })}
                      >
                        Intrekken
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirming != null}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={
          shown?.kind === "remove"
            ? `${shown.member.email} verwijderen?`
            : `Uitnodiging voor ${shown?.invitation.email ?? ""} intrekken?`
        }
        description={
          shown?.kind === "remove"
            ? "Dit lid verliest direct de toegang tot deze organisatie."
            : "De link in de uitnodiging werkt daarna niet meer."
        }
        confirmLabel={shown?.kind === "remove" ? "Verwijderen" : "Intrekken"}
        onConfirm={() =>
          shown?.kind === "remove"
            ? run(() => removeMember(organization, shown.member.id), "Lid verwijderd")
            : shown &&
              run(
                () => revokeInvitation(organization, shown.invitation.id),
                "Uitnodiging ingetrokken",
              )
        }
      />
      <InviteMemberModal
        open={inviting}
        onClose={(invited) => {
          setInviting(false);
          if (invited) {
            setLink({
              title: `Uitnodiging voor ${invited.email}`,
              url: invitationLink(invited.token),
              note: "Stuur deze link zelf naar de nieuwe beheerder. Hij werkt één keer en verloopt na 7 dagen. Een eerdere uitnodiging voor dit e-mailadres werkt dan niet meer.",
            });
            load();
          }
        }}
      />
      <LinkModal link={link} onClose={() => setLink(null)} />
    </div>
  );
};

export default Members;
